import { lazy, Suspense } from "react";
import { Routes, Route, Navigate } from "react-router";
import {
  StepGuard,
  InterviewRoomGuard,
  InterviewFallbackScreen,
  PreInterviewScreenSkeleton,
} from "./components/fallbacks";
import { CandidateDeviceGuard } from "./components/deviceCheck";
import { useInterviewFlow } from "./hooks/useInterviewFlow";
import { useLocation, useParams } from "react-router-dom";
import { useInterviewFlowState } from "@/features/interviewFlow";

// Lazy pages
const PreInterview1 = lazy(() => import("./pages/PreInterviewScreen1"));
const PreInterview2 = lazy(() => import("./pages/PreInterviewScreen2"));
const AfterInterview = lazy(() => import("./pages/AfterInterviewScreen"));
const InterviewRoom = lazy(() => import("./pages/InterviewRoom"));
const PanelPreInterviewScreen = lazy(
  () => import("./components/preInterviewScreen/PanelPreInterviewScreen"),
);

export default function App() {
  const location = useLocation();
  const { token } = useParams();
  const { loading, error, role, message } = useInterviewFlow();
  const { validationData } = useInterviewFlowState();

  const isInterviewRoom = location.pathname.includes("interviewRoom");
  const isPanelist = validationData?.role === "panel" || role === "panel";

  if (loading) {
    if (isInterviewRoom) {
      return <div className="min-h-screen bg-background" />;
    }
    return <PreInterviewScreenSkeleton />;
  }
  if (error) {
    return (
      <InterviewFallbackScreen
        variant={error}
        role={role || (isPanelist ? "panel" : "candidate")}
        customMessage={message}
      />
    );
  }

  return (
    <>
      <Suspense
        fallback={
          isInterviewRoom ? (
            <div className="min-h-screen bg-background" />
          ) : (
            <PreInterviewScreenSkeleton />
          )
        }
      >
        <Routes>
          <Route
            index
            element={
              isPanelist ? (
                <div className="auth-page">
                  <PanelPreInterviewScreen />
                </div>
              ) : (
                <CandidateDeviceGuard>
                  <StepGuard requiredStep={0}>
                    <PreInterview1 />
                  </StepGuard>
                </CandidateDeviceGuard>
              )
            }
          />

          <Route
            path="p2"
            element={
              isPanelist ? (
                <Navigate to={`/i/${token}`} replace />
              ) : (
                <CandidateDeviceGuard>
                  <StepGuard requiredStep={1}>
                    <PreInterview2 />
                  </StepGuard>
                </CandidateDeviceGuard>
              )
            }
          />

          {/* Other steps */}
          <Route path="conclude" element={<AfterInterview />} />
          <Route
            path="interviewRoom"
            element={
              <CandidateDeviceGuard>
                <InterviewRoomGuard>
                  <InterviewRoom />
                </InterviewRoomGuard>
              </CandidateDeviceGuard>
            }
          />
          <Route path="afterInterview" element={<AfterInterview />} />

          <Route path="*" element={<Navigate to="." replace />} />
        </Routes>
      </Suspense>
    </>
  );
}
